import { workspaceCommandSchema } from '@mio/shared';
import { z } from 'zod';

import {
  beginEditorSessionRecovery as sharedBeginEditorSessionRecovery,
  editorSessionFor as sharedEditorSessionFor,
  reclaimEditorSession as reclaimSharedEditorSession,
  rememberEditorSession as rememberSharedEditorSession,
  workshopEditorContextKey as editorContextKey,
} from '@/shared/workspace/editor-sessions';

import type { CurrentWorkspace } from '@mio/shared';

export const pendingCommandSchema = z
  .object({
    action: z.enum(['activate', 'checkpoint', 'stop', 'submit']),
    activityId: z.uuid().nullable(),
    command: workspaceCommandSchema,
    jobId: z.uuid().nullable(),
  })
  .strict();
export type PendingCommand = z.infer<typeof pendingCommandSchema>;

const key = (workspaceId: string, kind: string) => `mio.workspace.${workspaceId}.${kind}`;

/** This tab stores operation/session IDs only, never source, explanations, tokens or grants. */
export function readPendingCommand(workspaceId: string): PendingCommand | null {
  try {
    const parsed = pendingCommandSchema.safeParse(
      JSON.parse(sessionStorage.getItem(key(workspaceId, 'command')) ?? 'null'),
    );
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}
export function storePendingCommand(workspaceId: string, value: PendingCommand | null) {
  try {
    if (value) sessionStorage.setItem(key(workspaceId, 'command'), JSON.stringify(value));
    else sessionStorage.removeItem(key(workspaceId, 'command'));
  } catch {
    // The in-memory request remains retryable; storage-disabled reloads recover the server's busy job.
  }
}

export const editorSessionFor = (workspace: NonNullable<CurrentWorkspace['workspace']>) =>
  sharedEditorSessionFor(editorContextKey(workspace));
/** Launches adopt the accepted identity once the tab that owned it has closed. */
export const reclaimEditorSession = (workspace: NonNullable<CurrentWorkspace['workspace']>) =>
  reclaimSharedEditorSession(editorContextKey(workspace));
export const rememberEditorSession = (
  workspace: NonNullable<CurrentWorkspace['workspace']>,
  sessionId: string,
) => rememberSharedEditorSession(editorContextKey(workspace), sessionId);

export const beginEditorSessionRecovery = (workspace: NonNullable<CurrentWorkspace['workspace']>) =>
  sharedBeginEditorSessionRecovery(editorContextKey(workspace));

export function commandPath(request: PendingCommand) {
  switch (request.action) {
    case 'activate':
      if (!request.activityId) throw new Error('Choose an activity first.');
      return `/workshop/activities/${request.activityId}/activate`;
    case 'submit':
      if (!request.command.expectedAttemptId) throw new Error('Open an activity first.');
      return `/workshop/attempts/${request.command.expectedAttemptId}/submissions`;
    default:
      return `/workspaces/me/${request.action}`;
  }
}
